<template>
  <aside class="idf-newsletter-subscription__container" :class="containerStyle">
    <div v-if="$slots.illustration" class="idf-newsletter-subscription__illustration">
      <!-- @slot Use this slot to set the illustration -->
      <slot name="illustration"></slot>
    </div>
    <div class="idf-newsletter-subscription__main">
      <div class="idf-newsletter-subscription__main_content">
        <span class="idf-newsletter-subscription__icon adn-icon adn-icon--category-newsletter" />
        <div class="idf-newsletter-subscription__text">
          <h2 class="idf-newsletter-subscription__title">{{ title }}</h2>
          <p class="idf-newsletter-subscription__description">
            {{ content }}
          </p>
        </div>
      </div>
      <adn-button class="idf-newsletter-subscription__cta" kind="secondary" data-test-cta @click="onCtaClick">
        {{ cta }}
      </adn-button>
    </div>
    <adn-modal ref="newsletterModal" size="large" :autoclosable="true" @before-show="onBeforeShow">
      <template #body>
        <!-- @slot Use this slot to set the modal content -->
        <slot name="modalbody"></slot>
      </template>
    </adn-modal>
  </aside>
</template>

<script lang="ts">
import { computed, defineComponent, toRefs, ref, type Ref } from 'vue'
import AdnButton from '../AdnButton/AdnButton.vue'
import AdnModal from '../AdnModal/AdnModal.vue'

export default defineComponent({
  name: 'IdfNewsLetterSubscription',
  components: { AdnButton, AdnModal },
  props: {
    cta: {
      type: String,
    },
    title: {
      type: String,
    },
    content: {
      type: String,
    },
    mode: {
      type: String,
      default: 'inline',
      validator: (val: string) => ['inline', 'column', undefined].includes(val),
    },
  },
  setup(props, context) {
    const { mode } = toRefs(props)
    const newsletterModal: Ref<typeof AdnModal | null> = ref(null)
    const containerStyle = computed(() => {
      const classes: string[] = []
      if (mode.value === 'column') {
        classes.push('c-simple-cta-block__container--column')
      } else {
        classes.push('c-simple-cta-block__container--inline')
      }
      if (context.slots && context.slots?.illustration) {
        classes.push('c-simple-cta-block__container--with_image')
      } else {
        classes.push('c-simple-cta-block__container--without_image')
      }
      return classes
    })

    function onCtaClick() {
      context.emit('cta-click')
      // @ts-ignore: fausse erreur
      newsletterModal.value?.show()
    }

    function onBeforeShow() {
      context.emit('before-show-modal')
    }

    return {
      containerStyle,
      onBeforeShow,
      onCtaClick,
      newsletterModal,
    }
  },
})
</script>

<style lang="scss">
.idf-newsletter-subscription__container {
  @include apply-linear-gradient((var(--gradient-main-start), var(--gradient-main-end)), 90deg);

  border-radius: var(--border-radius-2);
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  .idf-newsletter-subscription__illustration {
    width: 100%;
    max-height: 15rem;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  .idf-newsletter-subscription__main {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .idf-newsletter-subscription__main_content {
      display: flex;
      color: var(--color-text-lightest);
      margin-bottom: var(--spacing-4);

      .idf-newsletter-subscription__icon {
        font-size: 2.4rem;
        margin-right: var(--spacing-2);
      }

      .idf-newsletter-subscription__text {
        .idf-newsletter-subscription__title {
          font-size: var(--font-size-xl);
          line-height: var(--line-height-xl);
          margin-bottom: var(--spacing-1);
        }

        .idf-newsletter-subscription__description {
          font-size: var(--font-size-s);
          line-height: var(--line-height-s);
        }
      }
    }

    .idf-newsletter-subscription__cta {
      width: 100%;
      flex: 1 0 auto;
    }
  }

  &.c-simple-cta-block__container--inline {
    @media (min-width: $breakpoint-md) {
      flex-direction: row;

      .idf-newsletter-subscription__main {
        flex-direction: row;
        flex: 1;

        .idf-newsletter-subscription__main_content {
          margin-bottom: 0;
          flex: 1 1 100%;

          .idf-newsletter-subscription__icon {
            font-size: 4.8rem;
            margin-right: 1.2rem;
          }
        }

        .idf-newsletter-subscription__cta {
          width: auto;
        }
      }

      &.c-simple-cta-block__container--with_image {
        .idf-newsletter-subscription__illustration {
          max-width: 33%;
          min-height: 100%;
        }

        .idf-newsletter-subscription__main {
          flex-direction: column;
          padding: 2rem;

          .idf-newsletter-subscription__main_content {
            margin-bottom: var(--spacing-3);
          }
        }
      }
    }
  }
}
</style>
